import React, { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { FiShield, FiCheckCircle } from "react-icons/fi";
import "./Recaptcha.css";

let apiPromise;
function loadApi() {
  if (!apiPromise) {
    apiPromise = new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        script.remove();
        apiPromise = null;
        reject(new Error("captcha"));
      }, 15000);
      window.ottCaptchaReady = () => {
        window.clearTimeout(timeout);
        resolve(window.grecaptcha);
      };
      const script = document.createElement("script");
      const language = (
        i18n.resolvedLanguage ||
        i18n.language ||
        "en"
      ).startsWith("ru")
        ? "ru"
        : "en";
      script.src = `https://www.google.com/recaptcha/api.js?onload=ottCaptchaReady&render=explicit&hl=${language}`;
      script.async = true;
      script.onerror = () => {
        window.clearTimeout(timeout);
        script.remove();
        apiPromise = null;
        reject(new Error("captcha"));
      };
      document.head.appendChild(script);
    });
  }
  return apiPromise;
}

export default function Recaptcha({ onChange, resetVersion }) {
  const container = useRef(null);
  const widget = useRef(null);
  const [enabled, setEnabled] = useState(null);
  const [verified, setVerified] = useState(false);
  const [message, setMessage] = useState("captcha_loading");
  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);
    onChange({ ready: false, token: "" });
    async function setup() {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACK_END}/captcha/config/`,
          { cache: "no-store", signal: controller.signal }
        );
        if (!response.ok) throw new Error("captcha");
        const config = await response.json();
        window.clearTimeout(timeout);
        if (typeof config.enabled !== "boolean") throw new Error("captcha");
        if (!active) return;
        setEnabled(config.enabled);
        if (!config.enabled) {
          setMessage("");
          onChange({ ready: true, token: "" });
          return;
        }
        if (!config.site_key) throw new Error("captcha");
        const api = await loadApi();
        if (!active) return;
        setMessage("captcha_required");
        widget.current = api.render(container.current, {
          sitekey: config.site_key,
          size:
            container.current.clientWidth < 304
              ? "compact"
              : "normal",
          callback: (token) => {
            if (active) {
              setMessage("captcha_verified_hint");
              setVerified(true);
              onChange({ ready: true, token });
            }
          },
          "expired-callback": () => {
            if (active) {
              setMessage("captcha_required");
              setVerified(false);
              onChange({ ready: false, token: "" });
            }
          },
          "error-callback": () => {
            if (active) {
              setMessage("captcha_unavailable");
              setVerified(false);
              onChange({ ready: false, token: "" });
            }
          },
        });
      } catch (_) {
        window.clearTimeout(timeout);
        if (active) {
          setMessage("captcha_unavailable");
          onChange({ ready: false, token: "" });
        }
      }
    }
    setup();
    return () => {
      active = false;
      controller.abort();
      window.clearTimeout(timeout);
      if (widget.current !== null && window.grecaptcha)
        window.grecaptcha.reset(widget.current);
      widget.current = null;
    };
  }, [onChange]);
  useEffect(() => {
    if (widget.current !== null && window.grecaptcha) {
      window.grecaptcha.reset(widget.current);
      setVerified(false);
      onChange({ ready: false, token: "" });
      setMessage("captcha_required");
    }
  }, [resetVersion, onChange]);
  if (enabled === false) return null;
  return (
    <section
      className={`captcha-field${verified ? " is-verified" : ""}`}
      aria-label={i18n.t("captcha_title")}
    >
      <div className="captcha-field__copy">
        <span className="captcha-field__eyebrow">{i18n.t("captcha_step")}</span>
        <div className="captcha-field__heading">
          {verified ? (
            <FiCheckCircle aria-hidden="true" />
          ) : (
            <FiShield aria-hidden="true" />
          )}
          <strong>
            {i18n.t(verified ? "captcha_verified" : "captcha_title")}
          </strong>
        </div>
        <p className="captcha-field__message" role="status">
          {i18n.t(message || "captcha_required")}
        </p>
      </div>
      <div className="captcha-field__widget" ref={container} />
    </section>
  );
}
